import type { ObjectAttributesEditorState } from '@/features/objects';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';

const LEAVE_MESSAGE = 'You have unsaved changes.';
const INVALID_LEAVE_MESSAGE =
  'You have unsaved changes. Discard them, or correct the errors on the page to save, to continue.';

type UseObjectEditorGuardOptions = {
  editor: ObjectAttributesEditorState;
  /** Resolves false when the write was rejected; the error is already on the page. */
  flow: { save: () => Promise<boolean>; hasUnsavedChanges: () => boolean };
  canConfigureObjects: boolean;
  /** Thrown out of the guard's save-and-continue when the write is rejected. */
  failureMessage: string;
};

/**
 * Arms the unsaved-changes guards for an object attributes editor and returns
 * the editor's own save handler, so the two share one blocked-save rule.
 */
export function useObjectEditorGuard({
  editor,
  flow,
  canConfigureObjects,
  failureMessage,
}: UseObjectEditorGuardOptions) {
  const isSaveBlocked = editor.isInvalid || !canConfigureObjects;

  const save = async () => {
    if (isSaveBlocked) return;
    await flow.save();
  };

  // The leave modal treats a resolved onSave as "safe to navigate", so a
  // rejected write must reject here — the error is already shown on the page.
  const saveOrThrow = async () => {
    if (isSaveBlocked) return;
    const didSave = await flow.save();
    if (!didSave) throw new Error(failureMessage);
  };

  const leaveGuard = {
    hasUnsavedChanges: flow.hasUnsavedChanges,
    message: editor.isInvalid ? INVALID_LEAVE_MESSAGE : LEAVE_MESSAGE,
    onSave: saveOrThrow,
    isSaveDisabled: isSaveBlocked,
    onDiscard: () => editor.reset(),
  };
  useNavigationGuard(leaveGuard);
  useHistoryLeaveBlocker(leaveGuard);

  return save;
}
